import { useEffect, useRef, useState } from 'react';

import { IconAlertCircle } from '@iwoca/icons';
import { LapiError } from '@iwoca/lapi-client/iwocapay';
import { Button as OrionButton } from '@iwoca/orion';

import {
  useGetFundingRequirement,
  useGetStateByStateKey,
  useRequestBuyerEmailOtp,
  useVerifyBuyerEmailOtp,
} from '../../../../api/lending/lapiHooks';
import { LoadingSpinner } from '../../../../components/LoadingSpinner/LoadingSpinner';
import { getPrimaryEmailAddressForCustomerState } from '../../../../Seller/AccessUsers/AccessUsers';
import { SubmitButton } from '../../../components/Button/Button';
import { Card } from '../../../components/Card/Card';
import { WhiteLabelOverride } from '../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { useNavigateToNextRequirement } from '../../hooks/useNavigateToNextRequirement';

const CODE_LENGTH = 6;

const EMPTY_CODE = Array.from({ length: CODE_LENGTH }, () => '');

export const EmailOtp = () => {
  const { goToNextRequirement, nextRequirementName } =
    useNavigateToNextRequirement();
  const { fetchFundingRequirements } = useGetFundingRequirement({
    queryOptions: { enabled: false },
  });
  const { state } = useGetStateByStateKey();
  const buyerEmail = getPrimaryEmailAddressForCustomerState(state);
  const { requestBuyerEmailOtp, requestingBuyerEmailOtp } =
    useRequestBuyerEmailOtp();
  const { verifyBuyerEmailOtp } = useVerifyBuyerEmailOtp();

  const [digits, setDigits] = useState<string[]>(EMPTY_CODE);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const sendCode = async () => {
    setErrorMessage(null);
    try {
      const response = await requestBuyerEmailOtp();
      setResendCooldown(response.data.retry_after_seconds);
    } catch (error: unknown) {
      if (error instanceof LapiError && error.statusCode === 429) {
        setErrorMessage(
          'Too many requests. Please wait a moment before trying again.',
        );
        return;
      }
      setErrorMessage('We could not send a code. Please try again.');
    }
  };

  useEffect(() => {
    void sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const code = digits.join('');
  const isCodeComplete = code.length === CODE_LENGTH;
  const isBusy = requestingBuyerEmailOtp || isSubmitting;
  const canContinue = isCodeComplete && !isBusy;

  const handleDigitChange = (index: number, value: string) => {
    const sanitised = value.replace(/\D/g, '');
    if (!sanitised) {
      setDigits((current) => {
        const next = [...current];
        next[index] = '';
        return next;
      });
      return;
    }

    setDigits((current) => {
      const next = [...current];
      for (let offset = 0; offset < sanitised.length; offset += 1) {
        const target = index + offset;
        if (target >= CODE_LENGTH) break;
        next[target] = sanitised[offset];
      }
      return next;
    });

    const nextIndex = Math.min(index + sanitised.length, CODE_LENGTH - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      await verifyBuyerEmailOtp(code);
      await fetchFundingRequirements();
      goToNextRequirement();
    } catch (error: unknown) {
      if (error instanceof LapiError && error.statusCode === 400) {
        setErrorMessage('That code is invalid. Please check and try again.');
      } else {
        setErrorMessage('We could not verify your code. Please try again.');
      }
      setDigits(EMPTY_CODE);
      inputRefs.current[0]?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const getButtonContent = () => {
    if (isSubmitting) return <LoadingSpinner />;
    return `Continue to ${nextRequirementName}`;
  };

  return (
    <Card title="Please verify your email">
      <p className="mb-l text-s font-med text-structure-40">
        Enter the 6-digit code we sent to{' '}
        {buyerEmail && buyerEmail !== 'Unknown' ? (
          <strong className="fs-mask text-primary-10">{buyerEmail}</strong>
        ) : (
          'your email'
        )}
      </p>
      <div className="mb-m flex gap-s">
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(element) => {
              inputRefs.current[index] = element;
            }}
            className="h-[50px] w-[42px] rounded-s border-2 border-structure-80 text-center font-[inherit] text-xl font-bold text-primary-10 outline-none focus:border-primary-40"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={index === 0 ? CODE_LENGTH : 1}
            value={digit}
            onChange={(event) => handleDigitChange(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            aria-label={`Digit ${index + 1}`}
          />
        ))}
      </div>
      {errorMessage ? (
        <p className="mb-m text-error" role="alert">
          {errorMessage}
        </p>
      ) : null}
      {requestingBuyerEmailOtp ? (
        <div className="mb-m flex h-l items-center">
          <span
            className="inline-block h-m w-m animate-loading-spinner-linear rounded-full border-2 border-primary-60/20 border-l-primary-60"
            role="status"
            aria-label="Sending code"
          />
        </div>
      ) : resendCooldown > 0 ? (
        <p className="mb-m text-s font-bold text-structure-60">
          Send another code in {resendCooldown}s
        </p>
      ) : (
        <OrionButton
          variant="tertiary"
          size="small"
          type="button"
          className="mb-m !h-auto !p-0 hover:!bg-transparent hover:underline"
          onClick={sendCode}
        >
          Send another code
        </OrionButton>
      )}
      <div className="mb-l flex items-center gap-xs rounded-s bg-secondary-97 p-s text-s font-med text-structure-40">
        <IconAlertCircle
          width={14}
          height={14}
          className="shrink-0"
          aria-hidden
        />
        Can't find it? Remember to check your spam/junk. The code can take a few
        minutes to arrive in your inbox.
      </div>
      <WhiteLabelOverride componentType="button" disabled={!canContinue}>
        <SubmitButton onClick={handleVerify} disabled={!canContinue}>
          {getButtonContent()}
        </SubmitButton>
      </WhiteLabelOverride>
    </Card>
  );
};
